import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, Card, Tag, Empty } from 'antd';
import { orderApi } from '../../services/process/order';
import { externalReportApi } from '../../services/process/external-report';
import { ExternalReport } from '../../types/process/external-report';
import { MergedOrder } from '../../types/process/order';
import BuerOverdueTab from './report/BuerOverdueTab';
import BuerXingyaoTab from './report/BuerXingyaoTab';
import RrzuBasicTab from './report/RrzuBasicTab';
import RrzuVideoTab from './report/RrzuVideoTab';
import ReportTabsBar from './report/components/ReportTabsBar';
import { getAgeFromIdCard, getGenderFromIdCard, maskIdCard, maskPhone } from './report/trans.effect';

const OrderReport: React.FC = () => {
  const { orderNo } = useParams<{ orderNo: string }>();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<MergedOrder | null>(null);
  const [reports, setReports] = useState<Record<string, ExternalReport>>({});
  const [activeKey, setActiveKey] = useState<string>('');
  const originalTitleRef = useRef<string>('');

  useEffect(() => {
    if (orderNo) {
      loadData(orderNo);
    }
  }, [orderNo]);

  useEffect(() => {
    if (!originalTitleRef.current) {
      originalTitleRef.current = document.title || '';
    }
    return () => {
      document.title = originalTitleRef.current || '';
    };
  }, []);

  useEffect(() => {
    const userName = order?.orderData?.userAuthName || order?.order?.userName || '';
    const nextTitle = userName
      ? `${userName} - 风控报告`
      : orderNo
        ? `${orderNo} - 风控报告`
        : '风控报告';
    document.title = nextTitle;
  }, [order?.order?.userName, order?.orderData?.userAuthName, orderNo]);

  const loadData = async (no: string) => {
    setLoading(true);
    try {
      const [orderData, reportList] = await Promise.all([
        orderApi.getDetail(no),
        externalReportApi.getReports(no)
      ]);
      setOrder(orderData);
      
      const map: Record<string, ExternalReport> = {};
      reportList.forEach(r => {
        map[r.reportType] = r;
      });
      setReports(map);

      const tabCandidates = [
        { key: 'risk', reportType: 'renrenzu_risk' },
        { key: 'video', reportType: 'renrenzu_video' },
        { key: 'overdue', reportType: 'buer_overdue' },
        { key: 'xingyao', reportType: 'buer_tianlangxing' },
      ];
      // Only select if status is 4 (Success)
      const firstAvailable = tabCandidates.find(t => map[t.reportType]?.status === 4)?.key;
      setActiveKey(firstAvailable || '');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!order) return <div style={{ textAlign: 'center', marginTop: 100 }}>未找到订单信息</div>;

  const { order: orderEntity, orderData } = order;
  const safeText = (v: any) => {
    const s = typeof v === 'string' ? v.trim() : String(v ?? '').trim();
    if (!s) return '';
    if (s === '-' || s === '--') return '';
    if (s === '-1' || s === 'null' || s === 'undefined') return '';
    return s;
  };

  const xingyaoContent: any = reports['buer_tianlangxing']?.content;
  const xingyaoResp: any =
    xingyaoContent && typeof xingyaoContent === 'object'
      ? xingyaoContent.resp_data && typeof xingyaoContent.resp_data === 'object'
        ? xingyaoContent.resp_data
        : xingyaoContent
      : null;
  const phoneAddress = safeText(xingyaoResp?.base_info?.phone_address);
  const identNumberAddress = safeText(xingyaoResp?.base_info?.ident_number_address);

  const age = orderData?.userAge ?? getAgeFromIdCard(orderEntity.userIdCard);
  const gender = orderData?.userSex ?? getGenderFromIdCard(orderEntity.userIdCard);

  const tabDefs = [
    { label: '风控报告', key: 'risk', reportType: 'renrenzu_risk', Component: RrzuBasicTab },
    { label: '交互式视频风控', key: 'video', reportType: 'renrenzu_video', Component: RrzuVideoTab },
    { label: '逾期探查', key: 'overdue', reportType: 'buer_overdue', Component: BuerOverdueTab },
    { label: '天狼星报告星耀版', key: 'xingyao', reportType: 'buer_tianlangxing', Component: BuerXingyaoTab },
  ];

  const renderContent = () => {
    if (!activeKey) return <Empty description="当前暂无可展示报告" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 40 }} />;
    const tab = tabDefs.find(t => t.key === activeKey);
    if (!tab) return null;
    const report = reports[tab.reportType];
    if (!report || !report.content) return <Empty description="暂无详细数据" />;
    const { Component } = tab;
    return <Component data={report.content} />;
  };

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <h1 style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 24 }}>风险报告综合版</h1>
        
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 32 }}>
           {/* Left Image */}
           <div style={{ marginRight: 32 }}>
             <img 
               src="/images/report.png" 
               alt="User" 
               style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover' }} 
             />
           </div>

           {/* Right Info */}
           <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <span style={{ fontSize: 24, fontWeight: 'bold', marginRight: 16 }}>
                  {orderEntity.userName && orderEntity.userName.length > 0 
                    ? orderEntity.userName.charAt(0) + '*' 
                    : orderEntity.userName}
                </span>
                <Tag color="blue" style={{ borderRadius: 12, padding: '0 12px' }}>年龄: {age}</Tag>
                <Tag color="blue" style={{ borderRadius: 12, padding: '0 12px' }}>性别: {gender}</Tag>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '12px 24px', color: '#666' }}>
                 <div>手机号：{maskPhone(orderEntity.userPhone)}</div>
                 <div>号码归属地：{phoneAddress}</div>
                 <div>身份证号：{maskIdCard(orderEntity.userIdCard)}</div>
                 <div>户籍：{identNumberAddress}</div>
              </div>
           </div>
        </div>

        {/* Custom Tabs */}
        <ReportTabsBar tabs={tabDefs} reports={reports} activeKey={activeKey} onChange={setActiveKey} />

        {/* Content */}
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
          {renderContent()}
        </Card>
      </div>
    </div>
  );
};

export default OrderReport;
